import React, { useEffect, useCallback, useRef, useState } from "react";
import {
  TbBrandInstagram,
  TbBrandTelegram,
  TbBrandWhatsapp,
  TbMessageCircle,
  TbPhoneCall,
} from "react-icons/tb";
import tenantCustomerService from "../../services/tenantCustomerService";
import styles from "./Customers.module.css";
import { ButtonBack, ButtonSave, IdentityBadge } from "../../components/ui";
import Modal from "../../components/Modal/Modal";
import { formatPhoneBR } from "../../utils/phone";
import {
  getSafeContactName,
  getSafePhone,
  getSafeUsername,
  looksLikeBsuid,
  resolveIdentityState,
} from "../../utils/identity";

// ─── Constantes ───────────────────────────────────────────────────────────────

const PLATFORM_LABEL = {
  whatsapp: "WhatsApp",
  whatsapp_bsuid: "WhatsApp",
  instagram: "Instagram",
  telegram: "Telegram",
};
const PLATFORM_ICON = {
  whatsapp: TbBrandWhatsapp,
  whatsapp_bsuid: TbBrandWhatsapp,
  instagram: TbBrandInstagram,
  telegram: TbBrandTelegram,
};
const PLATFORM_CLASS = {
  whatsapp: "channelWhatsapp",
  whatsapp_bsuid: "channelWhatsapp",
  instagram: "channelInstagram",
  telegram: "channelTelegram",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pt-BR");
}

function formatCurrency(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value ?? 0);
}

function getContactKey(contact, index) {
  return contact?.id ?? `${contact?.platform ?? "contact"}-${index}`;
}

function getContactSummary(customer) {
  const channelSummary = customer.contacts
    ?.map((contact) => PLATFORM_LABEL[contact.platform])
    .filter(Boolean)
    .join(", ");
  if (channelSummary) return channelSummary;
  const safePhone = getSafePhone(customer.phone);
  return safePhone ? formatPhoneBR(safePhone) : "sem contato";
}

function ChannelIcon({ platform }) {
  const Icon = PLATFORM_ICON[platform] ?? TbMessageCircle;
  return <Icon className={styles.channelIcon} aria-hidden="true" />;
}

function downloadCSV(customers) {
  const headers = [
    "Nome",
    "Telefone",
    "Instagram",
    "E-mail",
    "Aniversário",
    "Agendamentos",
    "Último agendamento",
  ];
  const rows = customers.map((c) => [
    getSafeContactName(c.name, ""),
    getSafePhone(c.phone),
    c.instagramHandle ? `@${c.instagramHandle}` : "",
    c.email ?? "",
    c.birthday ? formatDate(c.birthday) : "",
    c._count?.appointments ?? 0,
    formatDate(c.appointments?.[0]?.startTime),
  ]);
  const csv = [headers, ...rows]
    .map((r) =>
      r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `clientes-${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ name, src, size = 36 }) {
  const [imgError, setImgError] = useState(false);
  const displayName = getSafeContactName(name, "Cliente");
  const initials = displayName
    ? displayName
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={displayName}
        onError={() => setImgError(true)}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    );
  }

  const colors = ["#6e0ad4", "#0a84d4", "#d44e0a", "#0ad45a", "#d40a8c"];
  const bg = colors[(initials.charCodeAt(0) ?? 0) % colors.length];

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.38,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

// ─── Modal de mesclagem ───────────────────────────────────────────────────────

function MergeModal({ keepCustomer, allCustomers, onConfirm, onClose }) {
  const [removeId, setRemoveId] = useState("");
  const [saving, setSaving] = useState(false);

  const options = allCustomers.filter((c) => c.id !== keepCustomer.id);
  const keepCustomerName = getSafeContactName(keepCustomer.name, "este cliente");

  const handleConfirm = async () => {
    if (!removeId) return;
    setSaving(true);
    try {
      await onConfirm(removeId);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} width="460px" titleId="merge-modal-title">
      <div className={styles.modalInner}>
        <h3 id="merge-modal-title" className={styles.modalTitle}>🔀 Mesclar clientes</h3>
        <p className={styles.modalDesc}>
          Escolha o cadastro duplicado a ser removido. Todos os agendamentos,
          canais de contato e dados de perfil serão transferidos para{" "}
          <strong>{keepCustomerName}</strong>.
        </p>

        <div className={styles.modalField}>
          <label className={styles.modalLabel}>Cadastro a remover</label>
          <select
            className={styles.modalSelect}
            value={removeId}
            onChange={(e) => setRemoveId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>
                {getSafeContactName(c.name, "Cliente sem nome")}
                {c.instagramHandle ? ` @${c.instagramHandle}` : ""} —{" "}
                {getContactSummary(c)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.modalWarning}>
          ⚠️ Esta ação é irreversível. O cadastro selecionado será excluído
          permanentemente após a transferência dos dados.
        </div>

        <div className={styles.modalActions}>
          <button className={styles.btnCancel} onClick={onClose}>
            Cancelar
          </button>
          <button
            className={styles.btnMergeConfirm}
            onClick={handleConfirm}
            disabled={!removeId || saving}
          >
            {saving ? "Mesclando…" : "Confirmar mesclagem"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Painel de duplicatas ─────────────────────────────────────────────────────

function DuplicatesPanel({ onMerge, onClose }) {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tenantCustomerService
      .getDuplicates()
      .then(setSuggestions)
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={styles.modal}
        style={{
          width: 560,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3 className={styles.modalTitle} style={{ margin: 0 }}>
            🔍 Possíveis duplicatas
          </h3>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div className={styles.spinner} style={{ margin: "0 auto" }} />
          </div>
        )}

        {!loading && suggestions?.length === 0 && (
          <div className={styles.emptyState} style={{ padding: "30px 0" }}>
            <span className={styles.emptyIcon}>✅</span>
            <p>Nenhuma duplicata detectada!</p>
          </div>
        )}

        {!loading && suggestions?.length > 0 && (
          <div
            style={{
              overflowY: "auto",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {suggestions.map((s, i) => (
              <div key={i} className={styles.duplicateCard}>
                <div className={styles.duplicateCardHeader}>
                  <span
                    className={
                      s.confidence === "high"
                        ? styles.confidenceHigh
                        : styles.confidenceMedium
                    }
                  >
                    {s.confidence === "high"
                      ? "🔴 Alta confiança"
                      : "🟡 Média confiança"}
                  </span>
                  <span className={styles.duplicateReason}>{s.reason}</span>
                </div>
                <div className={styles.duplicateCustomers}>
                  <DuplicateCustomerCard customer={s.customerA} />
                  <span style={{ color: "#aaa", fontSize: 18 }}>⟷</span>
                  <DuplicateCustomerCard customer={s.customerB} />
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                    marginTop: 10,
                  }}
                >
                  <button
                    className={styles.btnSave}
                    style={{ fontSize: 12, padding: "6px 14px" }}
                    onClick={() => onMerge(s.customerA.id, s.customerB.id)}
                  >
                    Mesclar →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DuplicateCustomerCard({ customer }) {
  const displayName = getSafeContactName(customer.name, "Cliente sem nome");
  const safePhone = getSafePhone(customer.phone);

  return (
    <div className={styles.duplicateCustomerCard}>
      <strong style={{ fontSize: 13 }}>{displayName}</strong>
      {safePhone && (
        <span style={{ fontSize: 12, color: "#888" }}>
          {formatPhoneBR(safePhone)}
        </span>
      )}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
        {customer.contacts.map((c, index) => (
          <span
            key={getContactKey(c, index)}
            className={`${styles.channelBadge} ${styles[PLATFORM_CLASS[c.platform]] ?? ""}`}
            style={{ fontSize: 11, padding: "2px 7px" }}
          >
            <ChannelIcon platform={c.platform} />
            {PLATFORM_LABEL[c.platform]}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Componente de detalhe do cliente ─────────────────────────────────────────

function CustomerDetail({ customerId, onClose, onUpdated, onMergeRequest }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [requestInfoState, setRequestInfoState] = useState({
    loading: false,
    tone: null,
    message: "",
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    notes: "",
    instagramHandle: "",
    birthday: "",
    telegramUsername: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tenantCustomerService.getCustomerDetail(customerId);
      setCustomer(data);
      setRequestInfoState({ loading: false, tone: null, message: "" });
      setForm({
        name: getSafeContactName(data.name, ""),
        email: data.email ?? "",
        notes: data.notes ?? "",
        instagramHandle: data.instagramHandle ?? "",
        birthday: data.birthday
          ? new Date(data.birthday).toISOString().split("T")[0]
          : "",
        telegramUsername: data.telegramUsername ?? "",
      });
    } catch {
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await tenantCustomerService.updateCustomer(customerId, {
        ...form,
        birthday: form.birthday || null,
      });
      setEditing(false);
      await load();
      onUpdated?.();
    } catch (e) {
      setSaveError(
        e?.response?.data?.error ?? "Erro ao salvar. Tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRequestContactInfo = async () => {
    if (!customer?.id) return;
    setRequestInfoState({ loading: true, tone: null, message: "" });
    try {
      const data = await tenantCustomerService.requestContactInfo(customer.id);
      if (
        data?.status === "unavailable" &&
        data?.reason === "bsuid_send_disabled"
      ) {
        setRequestInfoState({
          loading: false,
          tone: "info",
          message:
            "Solicitar telefone estará disponível em breve para este contato.",
        });
        return;
      }
      setRequestInfoState({
        loading: false,
        tone: "success",
        message:
          "Pedido enviado. O cliente vai receber um botão para compartilhar o telefone.",
      });
    } catch (e) {
      const message =
        e?.response?.status === 409
          ? "Este contato já tem um telefone cadastrado."
          : e?.response?.data?.error ||
            "Não foi possível solicitar o telefone. Tente novamente.";
      setRequestInfoState({ loading: false, tone: "error", message });
    }
  };

  if (loading) {
    return (
      <div className={styles.detailPanel}>
        <div className={styles.detailHeader}>
          <span />
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        </div>
        <div className={styles.detailLoading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className={styles.detailPanel}>
        <div className={styles.detailHeader}>
          <p className={styles.detailName}>Erro ao carregar</p>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        </div>
      </div>
    );
  }

  const { stats } = customer;
  const displayName = getSafeContactName(customer.name, "Cliente sem nome");
  const safeUsername = getSafeUsername(customer.username);
  const customerIdentity = resolveIdentityState({
    identityState: customer.identityState,
    hasHiddenPhone: customer.hasHiddenPhone,
    phone: customer.phone,
    username: safeUsername,
  });
  const canRequestContactInfo = customerIdentity !== "phone";

  return (
    <div className={styles.detailPanel}>
      {/* Header com avatar */}
      <div className={styles.detailHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={displayName} src={customer.profilePicUrl} size={44} />
          <div>
            <h3 className={styles.detailName} style={{ margin: 0 }}>
              {displayName}
            </h3>
            {safeUsername && (
              <span className={styles.username}>@{safeUsername}</span>
            )}
            {customer.instagramHandle && (
              <span style={{ fontSize: 12, color: "#888" }}>
                @{customer.instagramHandle}
              </span>
            )}
          </div>
        </div>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
          ✕
        </button>
      </div>

      <div className={styles.detailBody}>
        {/* Campos editáveis */}
        <div className={styles.fieldGroup}>
          {editing ? (
            <>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Nome</span>
                <input
                  className={styles.fieldInput}
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>E-mail</span>
                <input
                  className={styles.fieldInput}
                  type="email"
                  placeholder="email@exemplo.com"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>@ Instagram</span>
                <input
                  className={styles.fieldInput}
                  placeholder="sem o @"
                  value={form.instagramHandle}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, instagramHandle: e.target.value }))
                  }
                />
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Aniversário</span>
                <input
                  className={styles.fieldInput}
                  type="date"
                  value={form.birthday}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, birthday: e.target.value }))
                  }
                />
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Telegram</span>
                <input
                  className={styles.fieldInput}
                  placeholder="@usuario_telegram"
                  value={form.telegramUsername}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, telegramUsername: e.target.value }))
                  }
                />
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Observações</span>
                <textarea
                  className={styles.fieldTextarea}
                  placeholder="Anotações sobre o cliente..."
                  value={form.notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, notes: e.target.value }))
                  }
                />
              </div>
              {saveError && (
                <p style={{ fontSize: 12, color: "#c53030", margin: 0 }}>
                  ⚠️ {saveError}
                </p>
              )}
              <div className={styles.editActions}>
                <button
                  className={styles.btnCancel}
                  onClick={() => setEditing(false)}
                >
                  Cancelar
                </button>
                <ButtonSave
                  isLoading={saving}
                  loadingText="Salvando…"
                  onClick={handleSave}
                >
                  Salvar
                </ButtonSave>
              </div>
            </>
          ) : (
            <>
              {customerIdentity === "phone" && getSafePhone(customer.phone) ? (
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Telefone</span>
                  <span className={styles.fieldValue}>
                    {formatPhoneBR(getSafePhone(customer.phone))}
                  </span>
                </div>
              ) : (
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Contato</span>
                  <span className={styles.fieldValue}>
                    <IdentityBadge
                      state={customerIdentity}
                      username={safeUsername}
                      phone={customer.phone}
                      size="md"
                    />
                  </span>
                </div>
              )}
              {canRequestContactInfo && (
                <div className={styles.requestPhoneRow}>
                  <button
                    type="button"
                    className={styles.btnRequestPhone}
                    onClick={handleRequestContactInfo}
                    disabled={requestInfoState.loading}
                    aria-busy={requestInfoState.loading}
                  >
                    <TbPhoneCall
                      className={styles.requestPhoneIcon}
                      aria-hidden="true"
                    />
                    {requestInfoState.loading
                      ? "Solicitando..."
                      : "Solicitar telefone"}
                  </button>
                  {requestInfoState.message && (
                    <p
                      className={`${styles.requestPhoneMsg} ${
                        styles[`requestPhone_${requestInfoState.tone}`] ?? ""
                      }`}
                      role="status"
                      aria-live="polite"
                    >
                      {requestInfoState.message}
                    </p>
                  )}
                </div>
              )}
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>E-mail</span>
                <span className={styles.fieldValue}>
                  {customer.email || "—"}
                </span>
              </div>
              {customer.instagramHandle && (
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Instagram</span>
                  <span className={styles.fieldValue}>
                    @{customer.instagramHandle}
                  </span>
                </div>
              )}
              {customer.birthday && (
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Aniversário</span>
                  <span className={styles.fieldValue}>
                    {formatDate(customer.birthday)}
                  </span>
                </div>
              )}
              {customer.telegramUsername && (
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Telegram</span>
                  <span className={styles.fieldValue}>
                    {customer.telegramUsername}
                  </span>
                </div>
              )}
              {customer.notes && (
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Observações</span>
                  <span className={styles.fieldValue}>{customer.notes}</span>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginTop: 2,
                }}
              >
                <button
                  className={styles.btnEdit}
                  onClick={() => setEditing(true)}
                >
                  Editar
                </button>
                {onMergeRequest && (
                  <button className={styles.btnMerge} onClick={onMergeRequest}>
                    🔀 Mesclar
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Canais de contato */}
        {customer.contacts?.length > 0 && (
          <>
            <p className={styles.sectionTitle}>Canais de contato</p>
            <div className={styles.contactChannels}>
              {customer.contacts.map((c, index) => (
                <span
                  key={getContactKey(c, index)}
                  className={`${styles.channelBadge} ${styles[PLATFORM_CLASS[c.platform]] ?? ""}`}
                  // Guarda da regra de ouro: nunca expor BSUID cru no tooltip.
                  title={
                    c.identifier && !looksLikeBsuid(c.identifier)
                      ? c.identifier
                      : undefined
                  }
                >
                  <ChannelIcon platform={c.platform} />
                  {PLATFORM_LABEL[c.platform] ?? c.platform}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Histórico de agendamentos — antes das stats para ficar acima da dobra */}
        {customer.appointments?.length > 0 && (
          <>
            <p className={styles.sectionTitle}>Histórico de agendamentos</p>
            <div
              style={{
                borderRadius: 8,
                border: "1px solid #ede9fb",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  background: "#f5f0ff",
                  padding: "7px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#6e0ad4",
                  textTransform: "uppercase",
                  letterSpacing: ".4px",
                }}
              >
                <span style={{ width: 110, flexShrink: 0 }}>Data</span>
                <span style={{ flex: 1 }}>Serviço</span>
                <span style={{ width: 100, flexShrink: 0 }}>Status</span>
              </div>
              {customer.appointments.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: "flex",
                    padding: "8px 10px",
                    borderTop: "1px solid #f0ecff",
                    fontSize: 12,
                    alignItems: "center",
                    background: "#fff",
                  }}
                >
                  <span style={{ width: 110, flexShrink: 0, color: "#555" }}>
                    {formatDate(a.startTime)}
                  </span>
                  <span style={{ flex: 1, color: "#444" }}>
                    {a.service?.name ?? "—"}
                  </span>
                  <span style={{ width: 100, flexShrink: 0 }}>
                    <span
                      className={`${styles.statusBadge} ${a.status === "confirmed" ? styles.statusConfirmed : styles.statusCancelled}`}
                    >
                      {a.status === "confirmed" ? "Confirmado" : "Cancelado"}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Stats */}
        {stats && (
          <>
            <p className={styles.sectionTitle}>Estatísticas</p>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statCardValue}>
                  {stats.totalAppointments}
                </div>
                <div className={styles.statCardLabel}>Agendamentos</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardValue}>
                  {stats.confirmedAppointments}
                </div>
                <div className={styles.statCardLabel}>Confirmados</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardValue}>
                  {stats.cancelledAppointments}
                </div>
                <div className={styles.statCardLabel}>Cancelados</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardValue}>
                  {formatCurrency(stats.totalRevenue)}
                </div>
                <div className={styles.statCardLabel}>Receita total</div>
              </div>
            </div>
          </>
        )}

        {/* Serviços usados */}
        {stats?.servicesUsed?.length > 0 && (
          <>
            <p className={styles.sectionTitle}>Serviços contratados</p>
            <div className={styles.servicesTags}>
              {stats.servicesUsed.map((s) => (
                <span key={s} className={styles.serviceTag}>
                  {s}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────────

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(null);
  const searchTimer = useRef(null);

  const fetchCustomers = useCallback(async (query = "") => {
    setLoading(true);
    setError(null);
    try {
      const data = await tenantCustomerService.listCustomers(query);
      setCustomers(data);
    } catch (e) {
      setError(e?.response?.data?.error ?? "Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  }, []);

  // Carrega na montagem e conta duplicatas para exibir badge
  useEffect(() => {
    fetchCustomers();
    tenantCustomerService
      .getDuplicates()
      .then((s) => setDuplicateCount(s.length))
      .catch(() => {});
  }, [fetchCustomers]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchCustomers(value), 350);
  };

  const handleRowClick = (id) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  const handleDetailClose = () => setSelectedId(null);

  const handleUpdated = () => fetchCustomers(search);

  const handleMergeConfirm = async (removeId) => {
    try {
      await tenantCustomerService.mergeCustomers(mergeTargetId, removeId);
      setMergeTargetId(null);
      setSelectedId(null);
      fetchCustomers(search);
      tenantCustomerService
        .getDuplicates()
        .then((s) => setDuplicateCount(s.length))
        .catch(() => {});
    } catch (e) {
      alert(e?.response?.data?.error ?? "Erro ao mesclar clientes. Tente novamente.");
    }
  };

  // Mescla a partir do painel de duplicatas (keepId = A, removeId = B)
  const handleDuplicateMerge = async (keepId, removeId) => {
    try {
      await tenantCustomerService.mergeCustomers(keepId, removeId);
      setShowDuplicates(false);
      fetchCustomers(search);
      tenantCustomerService
        .getDuplicates()
        .then((s) => setDuplicateCount(s.length))
        .catch(() => {});
    } catch (e) {
      alert(e?.response?.data?.error ?? "Erro ao mesclar clientes. Tente novamente.");
    }
  };

  const listClass = selectedId ? styles.listPanelNarrow : styles.listPanel;

  return (
    <div className={styles.page}>
      {/* Painel contextual de Clientes (busca + stats). No desktop fica à
          esquerda; no mobile empilha acima da lista. A navegação global vem
          da Sidebar do AppLayout. */}
      <aside className={styles.sidebar}>
        <ButtonBack to="/agenda" />
        <h2 className={styles.sidebarTitle}>Clientes</h2>

        <div className={styles.searchWrapper}>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Buscar por nome, telefone compartilhado ou @..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        {!loading && !error && (
          <div className={styles.sidebarStats}>
            <div className={styles.statItem}>
              <span>Total encontrado</span>
              <strong>{customers.length}</strong>
            </div>
            {duplicateCount > 0 && (
              <button
                className={styles.duplicatesBadgeBtn}
                onClick={() => setShowDuplicates(true)}
              >
                <span className={styles.duplicatesDot} />
                {duplicateCount} possíve
                {duplicateCount === 1 ? "l duplicata" : "is duplicatas"}
              </button>
            )}
          </div>
        )}
      </aside>

      {/* Main */}
      <div className={styles.main}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div>
            <h1 className={styles.pageTitle}>👥 Cadastro de Clientes</h1>
          </div>
          <div className={styles.toolbarActions}>
            {duplicateCount > 0 && (
              <button
                className={styles.btnDuplicates}
                onClick={() => setShowDuplicates(true)}
              >
                🔍 {duplicateCount} duplicata{duplicateCount > 1 ? "s" : ""}
              </button>
            )}
            {customers.length > 0 && (
              <button
                className={styles.btnExport}
                onClick={() => downloadCSV(customers)}
              >
                ⬇ Exportar CSV
              </button>
            )}
          </div>
        </div>

        {/* Conteúdo */}
        <div className={styles.contentArea}>
          {/* Painel de lista */}
          <div className={listClass}>
            {loading && (
              <div className={styles.loadingState}>
                <div className={styles.spinner} />
                <p>Carregando clientes…</p>
              </div>
            )}

            {error && !loading && (
              <div className={styles.errorState}>⚠️ {error}</div>
            )}

            {!loading && !error && customers.length === 0 && (
              <div className={styles.emptyState}>
                <span className={styles.emptyIcon}>👤</span>
                <p>
                  {search
                    ? "Nenhum cliente encontrado para essa busca."
                    : "Nenhum cliente cadastrado ainda."}
                </p>
                <p style={{ fontSize: 12, color: "#bbb" }}>
                  Os clientes são registrados automaticamente ao receber
                  mensagens ou criar agendamentos.
                </p>
              </div>
            )}

            {!loading && !error && customers.length > 0 && (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Nome</th>
                      <th>Contato</th>
                      <th>E-mail</th>
                      <th>Agendamentos</th>
                      <th>Último agendamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => {
                      const displayName = getSafeContactName(
                        c.name,
                        "Cliente sem nome",
                      );
                      const safeUsername = getSafeUsername(c.username);
                      const rowIdentity = resolveIdentityState({
                        identityState: c.identityState,
                        hasHiddenPhone: c.hasHiddenPhone,
                        phone: c.phone,
                        username: safeUsername,
                      });
                      return (
                      <tr
                        key={c.id}
                        onClick={() => handleRowClick(c.id)}
                        className={selectedId === c.id ? styles.rowActive : ""}
                      >
                        <td style={{ width: 44, paddingRight: 0 }}>
                          <Avatar
                            name={displayName}
                            src={c.profilePicUrl}
                            size={32}
                          />
                        </td>
                        <td>
                          <strong>{displayName}</strong>
                          {safeUsername && (
                            <div className={styles.usernameRow}>
                              @{safeUsername}
                            </div>
                          )}
                          {c.instagramHandle && (
                            <div style={{ fontSize: 11, color: "#9d174d" }}>
                              @{c.instagramHandle}
                            </div>
                          )}
                        </td>
                        <td>
                          {rowIdentity === "phone" && c.phone ? (
                            <span className={styles.phoneBadge}>
                              {formatPhoneBR(getSafePhone(c.phone))}
                            </span>
                          ) : (
                            <IdentityBadge
                              state={rowIdentity}
                              username={safeUsername}
                              phone={c.phone}
                              size="sm"
                            />
                          )}
                        </td>
                        <td>
                          <span className={styles.emailCell}>
                            {c.email ?? "—"}
                          </span>
                        </td>
                        <td>{c._count?.appointments ?? 0}</td>
                        <td>{formatDate(c.appointments?.[0]?.startTime)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Painel de detalhe */}
          {selectedId && (
            <CustomerDetail
              customerId={selectedId}
              onClose={handleDetailClose}
              onUpdated={handleUpdated}
              onMergeRequest={() => setMergeTargetId(selectedId)}
            />
          )}
        </div>
      </div>

      {/* Modal de mesclagem manual */}
      {mergeTargetId && (
        <MergeModal
          keepCustomer={
            customers.find((c) => c.id === mergeTargetId) ?? {
              id: mergeTargetId,
              name: "…",
            }
          }
          allCustomers={customers}
          onConfirm={handleMergeConfirm}
          onClose={() => setMergeTargetId(null)}
        />
      )}

      {/* Painel de duplicatas automáticas */}
      {showDuplicates && (
        <DuplicatesPanel
          onMerge={handleDuplicateMerge}
          onClose={() => setShowDuplicates(false)}
        />
      )}
    </div>
  );
}
